/**
 * useCurrentOrganization Hook
 *
 * Feature: 042-slack-bot-settings
 * Task: T072
 * Spec: contracts/hook-interfaces.md § useCurrentOrganization
 *
 * Wraps useQuery(MY_ORGANIZATIONS_QUERY) and returns the first organization.
 * Provides organizationId for all page hooks + role for FR-001 auth check.
 */

import { useQuery } from "@apollo/client/react";
import { MY_ORGANIZATIONS_QUERY } from "../graphql/queries/organization";

type OrganizationRole = "OWNER" | "ADMIN" | "MEMBER";

interface OrganizationWithRole {
  id: string;
  name: string;
  slug: string;
  myRole: OrganizationRole;
  memberCount: number;
}

interface MyOrganizationsData {
  myOrganizations: OrganizationWithRole[];
}

interface UseCurrentOrganizationReturn {
  organizationId: string | null;
  organizationName: string | null;
  role: OrganizationRole | null;
  loading: boolean;
  error: Error | undefined;
}

export function useCurrentOrganization(): UseCurrentOrganizationReturn {
  const { data, loading, error } = useQuery<MyOrganizationsData>(
    MY_ORGANIZATIONS_QUERY,
    { fetchPolicy: "cache-first" },
  );

  const org = data?.myOrganizations?.[0] ?? null;

  return {
    organizationId: org?.id ?? null,
    organizationName: org?.name ?? null,
    role: org?.myRole ?? null,
    loading,
    error,
  };
}
